import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FixedSizeList as List } from "react-window";

import { Box, Stack } from "@mui/material";
import { useNavigate } from "hooks";
import { DealBase, IFolder } from "store/types";

import { useWorkspace } from "../WorkspaceContext";
import { DealFolder } from "./DealFolder";
import { WorkspaceFolderRow } from "./WorkspaceFolderRow";

const ROW_HEIGHT = 36;

const slideInSx = {
  "@keyframes folderSlideIn": {
    from: { transform: "translateY(-6px)" },
    to: { transform: "translateY(0)" }
  },
  animation: "folderSlideIn 300ms ease"
};

// ----- folder tree helpers (same idea as VirtualizedFolderList) -----
function getRootFolders(folders: IFolder[]): IFolder[] {
  return folders.filter((f) => f.parent_id === null);
}

function getChildFolders(parentId: number, folders: IFolder[]): IFolder[] {
  return folders.filter((f) => f.parent_id === parentId);
}

function getVisibleFolderRows(
  folders: IFolder[],
  openedFolderIds: Set<number>,
  getRoot: (f: IFolder[]) => IFolder[],
  getChild: (id: number, f: IFolder[]) => IFolder[]
): Array<{ folder: IFolder; depth: number }> {
  const rows: Array<{ folder: IFolder; depth: number }> = [];
  const walk = (list: IFolder[], depth: number) => {
    for (const folder of list) {
      rows.push({ folder, depth });
      if (openedFolderIds.has(folder.id)) {
        walk(getChild(folder.id, folders), depth + 1);
      }
    }
  };
  walk(getRoot(folders), 0);
  return rows;
}

export type VisibleRow =
  | { type: "deal"; deal: DealBase }
  | { type: "folder"; folder: IFolder; depth: number; dealId: number };

function getVisibleRowId(row: VisibleRow): string {
  return row.type === "deal" ? `deal-${row.deal.id}` : `folder-${row.dealId}-${row.folder.id}`;
}

interface Props {
  availableHeight: number;
}

export const DealsFoldersList: React.FC<Props> = ({ availableHeight }) => {
  const {
    deals,
    foldersByDeal,
    openedDealIds,
    setOpenedDealIds,
    openedFolderIds,
    setOpenedFolderIds,
    currentFolderId,
    currentDealId,
    getOrLoadDealFolders
  } = useWorkspace();

  const navigate = useNavigate();

  const toggleDeal = useCallback(
    (dealId: number) => {
      setOpenedDealIds((prev) => {
        const next = new Set(prev);
        if (next.has(dealId)) {
          next.delete(dealId);
        } else {
          next.add(dealId);
        }
        return next;
      });
    },
    [setOpenedDealIds]
  );

  const toggleFolder = useCallback(
    (folderId: number) => {
      setOpenedFolderIds((prev) => {
        const next = new Set(prev);
        if (next.has(folderId)) {
          next.delete(folderId);
        } else {
          next.add(folderId);
        }
        return next;
      });
    },
    [setOpenedFolderIds]
  );

  const onDealClick = useCallback(
    (dealId: number) => navigate({ route: "workspaceDeal", params: { deal_id: dealId } }),
    []
  );

  const visibleRows = useMemo<VisibleRow[]>(() => {
    const rows: VisibleRow[] = [];
    for (const deal of deals) {
      rows.push({ type: "deal", deal });
      if (openedDealIds.has(deal.id)) {
        const folders = foldersByDeal.get(deal.id) ?? [];
        const folderRows = getVisibleFolderRows(folders, openedFolderIds, getRootFolders, getChildFolders);
        for (const { folder, depth } of folderRows) {
          rows.push({ type: "folder", folder, depth, dealId: deal.id });
        }
      }
    }
    return rows;
  }, [deals, openedDealIds, foldersByDeal, openedFolderIds]);

  const prevVisibleIdsRef = useRef<Set<string>>(new Set(visibleRows.map(getVisibleRowId)));
  const [newlyVisibleIds, setNewlyVisibleIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    const currentIds = new Set(visibleRows.map(getVisibleRowId));
    const prev = prevVisibleIdsRef.current;
    const newIds = new Set(Array.from(currentIds).filter((id) => !prev.has(id)));
    prevVisibleIdsRef.current = currentIds;
    if (newIds.size === 0) return;
    setNewlyVisibleIds(newIds);
    const timer = setTimeout(() => setNewlyVisibleIds(new Set()), 500);
    return () => clearTimeout(timer);
  }, [visibleRows]);

  const itemData = useMemo(
    () => ({
      visibleRows,
      foldersByDeal,
      openedDealIds,
      openedFolderIds,
      toggleDeal,
      toggleFolder,
      onDealClick,
      newlyVisibleIds,
      getOrLoadDealFolders,
      currentFolderId,
      currentDealId
    }),
    [
      visibleRows,
      foldersByDeal,
      openedDealIds,
      openedFolderIds,
      toggleDeal,
      toggleFolder,
      onDealClick,
      newlyVisibleIds,
      getOrLoadDealFolders,
      currentFolderId,
      currentDealId
    ]
  );

  if (deals.length === 0 || availableHeight <= 0) {
    return null;
  }

  return (
    <Stack
      sx={{
        width: "100%",
        paddingLeft: "25px",
        height: availableHeight,
        minHeight: 0
      }}
    >
      <List
        height={availableHeight}
        width="100%"
        itemCount={visibleRows.length}
        itemSize={ROW_HEIGHT}
        overscanCount={10}
        itemData={itemData}
      >
        {({ index, style, data }) => {
          const row = data.visibleRows[index];
          const rowId = getVisibleRowId(row);
          const isNew = data.newlyVisibleIds.has(rowId);
          return (
            <Box style={style} sx={isNew ? slideInSx : undefined}>
              {row.type === "deal" ? (
                <DealFolder
                  deal={row.deal}
                  isOpen={data.openedDealIds.has(row.deal.id)}
                  isSelected={data.currentDealId === row.deal.id && data.currentFolderId == null}
                  hasFoldersLoaded={data.foldersByDeal.has(row.deal.id)}
                  getOrLoadDealFolders={data.getOrLoadDealFolders}
                  onToggle={data.toggleDeal}
                  onDealClick={data.onDealClick}
                />
              ) : (
                <WorkspaceFolderRow
                  folder={row.folder}
                  depth={row.depth}
                  folders={data.foldersByDeal.get(row.dealId) ?? []}
                  isOpen={data.openedFolderIds.has(row.folder.id)}
                  isSelected={data.currentFolderId === row.folder.id}
                  toggleFolder={data.toggleFolder}
                />
              )}
            </Box>
          );
        }}
      </List>
    </Stack>
  );
};
